/**
 * Live validation panel (spec 2026-08-22-02 §11) — a port of the exp editor's
 * Issues list. Rows are clickable: selecting one selects the offending element
 * and pans it into view.
 *
 * Messages are translated from the issue's code + params, never built as
 * strings in the engine, so every one of them exists in EN and FR.
 */
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiAlertCircleOutline, mdiAlertOutline, mdiCallSplit, mdiCheckCircleOutline, mdiInformationOutline } from '@mdi/js'
import { Button } from '../ui/button'
import type { IssueSeverity, ValidationIssue } from './engine/validation'

/**
 * The issue codes spec 2026-08-25-04 §7 names explicitly. Their rows carry the
 * spec's own test ids rather than the generic `plan-editor-issue-<code>` so the
 * e2e contract does not depend on an internal code string.
 */
const NAMED_TESTIDS: Record<string, string> = {
    split_needed: 'plan-editor-split-warning',
    duplicate_layer: 'plan-editor-duplicate-layer-warning',
}

const ICONS: Record<IssueSeverity, string> = {
    error: mdiAlertCircleOutline,
    warning: mdiAlertOutline,
    info: mdiInformationOutline,
}

const TONES: Record<IssueSeverity, string> = {
    error: 'text-destructive',
    warning: 'text-amber-600 dark:text-amber-400',
    info: 'text-muted-foreground',
}

interface ValidationPanelProps {
    issues: ValidationIssue[]
    onSelect: (key: string) => void
    /**
     * One click from the split warning into the promote flow (§4): selecting
     * the offending element and giving it its own asset in a single gesture.
     */
    onPromote?: (key: string) => void
}

export function ValidationPanel({ issues, onSelect, onPromote }: ValidationPanelProps) {
    const { t } = useTranslation('assets')

    return (
        <div className="border-t border-border" data-testid="plan-editor-validation-panel">
            <div className="flex items-center gap-2 px-3 py-2">
                <h2 className="text-sm font-semibold text-foreground">{t('planEditor.validation_title')}</h2>
                <span className="text-xs text-muted-foreground" data-testid="plan-editor-validation-count">
                    {issues.length}
                </span>
            </div>
            {issues.length === 0 ? (
                <div
                    className="flex items-center gap-2 px-3 pb-3 text-xs text-muted-foreground"
                    data-testid="plan-editor-validation-empty"
                >
                    <AppIcon path={mdiCheckCircleOutline} size="sm" />
                    {t('planEditor.validation_empty')}
                </div>
            ) : (
                <ul className="max-h-40 overflow-y-auto pb-2">
                    {issues.map((issue, i) => (
                        <li key={`${issue.code}-${issue.elementKey ?? i}`} className="flex items-start">
                            <button
                                type="button"
                                className="flex min-w-0 flex-1 items-start gap-2 px-3 py-1 text-left text-xs hover:bg-muted"
                                data-testid={NAMED_TESTIDS[issue.code] ?? `plan-editor-issue-${issue.code}`}
                                onClick={() => issue.elementKey && onSelect(issue.elementKey)}
                            >
                                <AppIcon path={ICONS[issue.severity]} size="xs" className={`mt-0.5 shrink-0 ${TONES[issue.severity]}`} />
                                <span className={TONES[issue.severity]}>
                                    {t(`planEditor.issues.${issue.code}`, { ...(issue.params ?? {}) }) as string}
                                </span>
                            </button>
                            {issue.code === 'split_needed' && issue.elementKey && onPromote && (
                                // Icon-only: the panel is 288 px wide and the
                                // message is what the reader needs, not the verb.
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="mr-2 mt-0.5 h-6 w-6 shrink-0"
                                    aria-label={t('planEditor.promote')}
                                    title={t('planEditor.promote_hint')}
                                    data-testid="plan-editor-split-warning-promote"
                                    onClick={() => onPromote(issue.elementKey as string)}
                                >
                                    <AppIcon path={mdiCallSplit} size="xs" />
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
